<template>
  <!------------------------------------------
    Grantee list for the web version of the congressional state profiles.
  -------------------------------------------->
  <div id="congr-state-web-grantees">
    <div>
      <div class="grantee-list-intro" v-if="$vuetify.breakpoint.smAndDown === false">
        <span v-if="Object.keys(granteesByType.bfGeneral).length > 1">Select a grantee to learn more.</span> 
        <!-- Mouse over an office on the map for more information. -->
      </div>
      <div class="grantee-list">
        <div class="grantee-list-item"
        v-for="(g, index) in granteesByType.bfGeneral.filter((grantee, i, arr) => arr.findIndex(t => t.org_name === grantee.org_name) === i)" 
        :key="index" :id="'list-item-' + g.recipient_id"  
        :class="Object.keys(granteesByType.bfGeneral).length > 1 ? '' : 'single-grantee'"
        >
          <a :href="selectedOrgProfileLink(g.recipient_id)" target="_blank">
            <div
              title="Click to learn more about this grantee" 
              class="grantee-list-item-header"
              :id="'list-item-header-' + g.recipient_id" 
            >
              <v-icon size="12" left :color="colorPerServArea.filter(x => x.area === g.serv_area)[0].color">
                {{ circleIcon }}
              </v-icon>
              <div>
                {{ g.org_name }}
                <span class="a-n-symbol" v-if="granteesByType.agWorker.map(x => x.org_name).indexOf(g.org_name) > -1">(A)</span>
                <span class="a-n-symbol" v-if="granteesByType.nativeAm.map(x => x.org_name).indexOf(g.org_name) > -1">(N)</span>
              </div>
            </div>
          </a>
          <div class="main-office" v-if="granteesByType.bfGeneral.length < 4">
            Main office: 
            <span class="main-office-name">{{ mainOfficePerGrantee[g.recipient_id] }}</span>
            <v-icon color="#b31308" size="18">
              {{ mainOfficeIcon }}
            </v-icon>
          </div>
          <div class="branch-offices" v-if="granteesByType.bfGeneral.length === 1 && g.recipient_id in sortedBranchesPerGrantee && sortedBranchesPerGrantee[g.recipient_id].length > 0">
            Branch offices: <span class="main-office-name">{{ sortedBranchesPerGrantee[g.recipient_id].join(', ') }}</span>
          </div>
        </div>
        <div class="other-a-n-grantee" v-if="outOfStateBothAgAndNativeAm.length > 0">
          <div>Also serving Native Americans and agricultural workers:</div>
          <div class="grantee-list-item-header other-area-type" v-for="g in outOfStateBothAgAndNativeAm" :key="g.org_name">
            <v-icon size="12" left color="white">{{ circleIcon }}</v-icon>
            <a :href="selectedOrgProfileLink(g.recipient_id)" target="_blank">
              <div>{{ g.org_name }}</div>
            </a>
          </div>
        </div>
        <div class="other-a-n-grantee" v-if="outOfStateAgWorkerNotBoth.length > 0">
          <div>Also serving agricultural workers:</div>
          <div class="grantee-list-item-header other-area-type" v-for="g in outOfStateAgWorkerNotBoth" :key="g.org_name">
            <v-icon size="12" left color="white">{{ circleIcon }}</v-icon>
            <a :href="selectedOrgProfileLink(g.recipient_id)" target="_blank">
              <div>{{ g.org_name }}</div>
            </a>
          </div>
        </div>
        <div class="other-a-n-grantee" v-if="outOfStateNativeAmNotBoth.length > 0">
          <div>Also serving Native Americans:</div>
          <div class="grantee-list-item-header other-area-type" v-for="g in outOfStateNativeAmNotBoth" :key="g.org_name">
            <v-icon size="12" left color="white">{{ circleIcon }}</v-icon>
            <a :href="selectedOrgProfileLink(g.recipient_id)" target="_blank">
              <div>{{ g.org_name }}</div>
            </a>
          </div>
        </div>
        <div class="grantee-list-footer" v-if="agWorkerGrantHeldByBFGeneralRecipient === true || nativeAmGrantHeldByBFGeneralRecipient === true">
          <div v-if="agWorkerGrantHeldByBFGeneralRecipient === true"><div class="a-n-symbol">(A)</div><div class="grantee-list-footer-t">Agricultural worker grant</div></div>
          <div v-if="nativeAmGrantHeldByBFGeneralRecipient === true"><div class="a-n-symbol">(N)</div><div class="grantee-list-footer-t">Native American grant</div></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mdiCircle, mdiStarOutline } from '@mdi/js';
import grantees from '@/js/grantees.js'

export default {
  name: 'StateProfilesWebGranteeList',

  props: {
    selectedState: { type: String, required: true },
    granteesByType: { type: Object, required: true },
    colorPerServArea: { type: Array, required: true },
    officesPerGrantee: { type: Object, required: true }
  },

  data () {
    return {
      circleIcon: mdiCircle,
      mainOfficeIcon: mdiStarOutline,
      selectedOrg: null,
      grantees: grantees
    }
  },

  computed: {
    outOfStateBothAgAndNativeAm () {
      // An out of state grantee (having no BF General grant in the state) may have both the ag worker and native american grants, as with Pine Tree in Connecticut.
      return this.granteesByType.nativeAmNotBfGeneral.filter(g => this.granteesByType.agWorkerNotBfGeneral.map(x => x.org_name).indexOf(g.org_name) > -1)
    },

    outOfStateAgWorkerNotBoth () {
      return this.granteesByType.agWorkerNotBfGeneral.filter(g => this.outOfStateBothAgAndNativeAm.map(y => y.org_name).indexOf(g.org_name) === -1)
    },

    outOfStateNativeAmNotBoth () {
      return this.granteesByType.nativeAmNotBfGeneral.filter(g => this.outOfStateBothAgAndNativeAm.map(y => y.org_name).indexOf(g.org_name) === -1)
    },

    agWorkerGrantHeldByBFGeneralRecipient () {
      return this.granteesByType.agWorker.length > 0 && this.granteesByType.agWorkerNotBfGeneral.length === 0
    },

    nativeAmGrantHeldByBFGeneralRecipient () {
      return this.granteesByType.nativeAm.length > 0 && this.granteesByType.nativeAmNotBfGeneral.length === 0
    },

    mainOfficePerGrantee () {
      let mainOffices = {}
      for (const rno of Object.keys(this.officesPerGrantee)) {
        let main = this.officesPerGrantee[rno].features.filter(y => y.properties.type === 'Main')
        if (main.length > 0) { mainOffices[rno] = main[0].properties.city }
      }
      return mainOffices
    },

    sortedBranchesPerGrantee () {
      let branchOffices = {}
      for (const rno of Object.keys(this.officesPerGrantee)) {
        let branchFeatures = this.officesPerGrantee[rno].features.filter(y => y.properties.type === 'Branch')
        if (branchFeatures.length === 0) {
          branchOffices[rno] = []
        } else {
          branchOffices[rno] = branchFeatures.map(x => x.properties.city).sort()
        }
      }
      return branchOffices
    },
  },

  methods: {
    // selectOrg (rno) {
    //   // If same as selected, just deselect
    //   if (rno === this.selectedOrg) {
    //     let d = document.getElementById(`list-item-${rno}`)
    //     d.classList.remove('selected-grantee')
    //     this.selectedOrg = null
    //     this.$emit('orgDeselected', rno)
    //     return
    //   }

    //   // Else clear any already selected and select the new one
    //   else {
    //     const items = document.querySelectorAll('.grantee-list-item')
    //     items.forEach(s => {
    //       s.classList.remove('selected-grantee');
    //     })

    //     this.selectedOrg = rno
    //     let d = document.getElementById(`list-item-${rno}`)
    //     d.classList.add('selected-grantee')
    //     this.$emit('orgSelected', rno)
    //   }
    // },
    selectedOrgProfileLink (rno) {
      let node = this.grantees.filter(x => x.value === rno)[0].lscDotGovProfileNode
      return `https://www.lsc.gov/node/${node}`
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/scss/vars.scss';
#congr-state-web-grantees {
  padding-top: 1rem;
  display: flex;
  flex-direction: column;
  height: 100%;

  .grantee-list-intro {
    font-size: 1rem;
    margin: 0 1.5rem 1rem 1.5rem;
    font-style: italic;
  }

  .grantee-list-item {
    padding: 0 0.5rem;
  }
  .single-grantee .grantee-list-header {
    pointer-events: none;
  }
  .grantee-list {
    display:flex;
    flex-direction: column;
    
    .item-color {
      width: 2rem;
      height: 2rem;
    }
  }
  .grantee-list-item-header {
    padding: 0.25rem 2rem 0.25rem 0.5rem;
    margin: 0;
    display: flex;
    align-items: baseline;
    cursor: pointer;
    border-radius: 5px;
    min-height: 1.25rem;
    line-height: 1.5;
    background-color: transparent;
    font-size: 1rem;
    transition: all 0.1s ease-in-out;
    clip-path: polygon(0 0, calc(100% - 1rem) 0, 100% 50%, 100% 50%, calc(100% - 1rem) 100%, 0 100%);

    &:hover {
      border: 0;
      cursor: pointer;
      font-weight: 700;
      text-decoration: none !important;
      transition: 0.2s ease-out;
      background-color: #b10000;
      color: #fff !important;

      .a-n-symbol {
        color: #fff;
      }
    }
  }
  .selected-grantee .grantee-list-item-header {
    border-right: 0.1rem solid rgba(22, 89, 145, 0.2);
    border-bottom: 0.1rem solid rgba(22, 89, 145, 0.2);
    background-color: $extra-subtle-blue;
  }
  .grantee-list-footer {
    margin-top: 1rem;
    display: flex;
    height: 1rem;
    
    >div {
      margin-left: 1rem;
      display: flex;
      align-items: baseline;
    }
    .grantee-list-footer-t {
      font-style: italic;
      font-size: 0.9rem;
      padding-left: 0.25rem;
      color: #1a1a1a;
    }
  }
  .other-a-n-grantee {
    padding-top: 1rem;
    margin-left: 0.5rem;

    >div:first-child {
      font-style: italic;
      margin-left: 0.5rem;
    }
  }
  .a-n-symbol {
    display: inline-block;
    min-width: 1rem;
    max-width: 1rem;
    min-height: 1rem;
    max-height: 1rem;
    font-size: 0.9rem;
    font-style: italic;
    text-align: center;
    font-weight: 400;
    color: #1a1a1a;
    opacity: 0.75;
    margin: 0 0.125rem;
  }
  .main-office .v-icon {
    margin-top: -0.25rem;
    margin-left: 0.25rem;
  }
  .main-office, .branch-offices {
    font-size: 0.9rem;
    color: #5a5a5a;
    margin-left: 2rem;
    padding-top: 0.5rem;

    &:last-of-type {
      padding-bottom: 0.5rem;
    }
  }
  .branch-offices {
    margin-bottom: 1rem;
  }
  .link-to-grantee-profile {
    margin: 0.25rem 0 0.25rem 2rem;
    pointer-events: all;
    display: none;
  }
  .single-grantee .link-to-grantee-profile, .selected-grantee .link-to-grantee-profile {
    display: block;
  }
}
</style>